// EVALUATION REPORT — PRESENTATIONAL BODY
// Extracted from VisibilityReport.jsx so the same report UI can be rendered
// from two data sources: the sessionStorage-backed /results route
// (VisibilityReport.jsx) and the fetch-by-id public /evaluation/:id route
// (EvaluationResultPage.jsx). Takes the already-loaded evaluation data as a
// prop; has no opinion on where that evaluation data came from.
//
// One exception to "presentational only": the price shown in the CTA block is
// fetched here from /api/pricing/current, the same admin-controlled source
// CheckoutPage.jsx reads, so the report can never quote a price the checkout
// won't honour. It is deliberately NOT seeded with a default — see the
// `pricing` state below.
//
// Visual design: Poppins + purple/navy palette (design system 33bd7d1f),
// reusing NewLanding.jsx's `c` token names 1:1 for site-wide consistency.

import API_BASE from '../config';
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, X, ChevronDown, Search, Loader2, Lock } from 'lucide-react';
import { trackDwell, trackEvent } from '../services/journeyTracking';

const API_URL = API_BASE;

const c = {
  text:      '#1a1130',
  textMid:   '#4b4560',
  textSoft:  '#5b5470',
  textMute:  '#6b6580',
  textDim:   '#9691a8',
  purpleSoft:'#8a7fb0',
  purple:    '#7c3aed',
  purpleDeep:'#6d28d9',
  purpleMid: '#8b5cf6',
  gradient:  'linear-gradient(135deg, #8b5cf6, #7c3aed)',
  hilite:    'linear-gradient(90deg, #c4b5fd, #8b5cf6)',
  navy:      '#1a0c38',
  navy2:     '#2a1257',
  navyMid:   '#4a2691',
  bgWhite:   '#ffffff',
  bgLavender:'#f6f4fd',
  bgFaint:   '#efedf6',
  green:     '#22c55e',
  greenDeep: '#16a34a',
  greenLite: '#dcfce7',
  yellow:    '#fbbf24',
  red:       '#ef4444',
  redDeep:   '#dc2626',
  redLite:   '#fee2e2',
  amber:     '#f59e0b',
  amberDeep: '#b45309',
  amberLite: '#fef3c7',
};

const ROLE_ACRONYMS = ['ceo','cto','cfo','coo','cmo','chro','cio','vp','svp','avp','pm','hr','qa','sde','ml','ai','ui','ux'];
const displayRole = (role) =>
  (role || '')
    .split(' ')
    .map(w => ROLE_ACRONYMS.includes(w.toLowerCase())
      ? w.toUpperCase()
      : w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');

// Initials for the "your profile" avatar chip in the search-rankings mock —
// first letter of the first name + first letter of the last/middle name
// (e.g. "Hemant K." -> "HK"), matching how LinkedIn's own initials avatars
// are built. Falls back to the first two letters of a single-word name.
const getInitials = (fullName) => {
  const parts = (fullName || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '??';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const SECTION_LABELS = {
  headline: 'Headline',
  summary: 'Summary',
  experience: 'Experience',
  keywords: 'Keywords',
  recruiter_search_index: 'Search Index'
};
const SECTION_KEYS = ['keywords', 'headline', 'summary', 'experience', 'recruiter_search_index'];

// Parses "😔 Not Found" -> { emoji: '😔', text: 'Not Found', kind: 'bad' }.
// Reads the backend's own emoji choice (profile_evaluation_v5.py's
// _get_callout_verdict) rather than recomputing the 70/40% tier thresholds
// independently on the frontend — this always matches the backend's own
// tier exactly, with zero duplicated business logic.
const STATE_EMOJI_KIND = { '😔': 'bad', '😐': 'mid', '😊': 'good' };
const parseStateLabel = (stateLabel) => {
  if (!stateLabel) return { emoji: '🔍', text: '', kind: 'mid' };
  const [emoji, ...rest] = stateLabel.trim().split(' ');
  return { emoji, text: rest.join(' '), kind: STATE_EMOJI_KIND[emoji] || 'mid' };
};

const KIND_STYLES = {
  bad:  { badgeBg: c.redLite,   badgeColor: c.redDeep,   bar: c.red },
  mid:  { badgeBg: c.amberLite, badgeColor: c.amberDeep, bar: c.amber },
  good: { badgeBg: c.greenLite, badgeColor: c.greenDeep, bar: c.green },
};

// ==========================================
// WEAKEST SECTION — drives the returning-user banner's gap line.
// Reads full_result.judge_meta.verdicts.q1..q17 (each {verdict, evidence}),
// maps verdict -> points, averages per section, returns the lowest. Falls
// back to section_scores (note: its key is recruiter_search_index, not
// search_index) if judge_meta is missing/malformed, then to 'experience'.
// ==========================================
const VERDICT_POINTS = { STRONG: 100, GOOD: 80, PARTIAL: 50, WEAK: 25, MISSING: 0 };
const SECTION_QUESTION_GROUPS = {
  experience:   ['q7', 'q8', 'q9', 'q10'],
  headline:     ['q1', 'q2', 'q3'],
  summary:      ['q4', 'q5', 'q6'],
  keywords:     ['q11'],
  search_index: ['q15', 'q16'],
};
// Tie-break order, also used as the fallback-path iteration order.
const TIE_BREAK_ORDER = ['experience', 'headline', 'summary', 'keywords', 'search_index'];

const SECTION_GAP_LINES = {
  headline: "Your headline doesn't say what seat you're targeting, so you don't surface when a recruiter searches for it.",
  summary: "Your summary reads as description rather than evidence. There's nothing in it a recruiter can act on.",
  experience: "Your recent roles read as job titles without evidence underneath them. There's nothing specific to hold on to.",
  keywords: "The terms recruiters actually search for your role aren't on your profile. The search doesn't reach you.",
  search_index: "Your profile is complete but not structured the way recruiter search reads it, so it ranks below profiles with less behind them.",
};

function computeWeakestSection(result) {
  const verdicts = result?.judge_meta?.verdicts;
  if (verdicts && typeof verdicts === 'object') {
    const scores = {};
    let ok = true;
    for (const [section, qs] of Object.entries(SECTION_QUESTION_GROUPS)) {
      const pts = qs.map(q => VERDICT_POINTS[verdicts[q]?.verdict]);
      if (pts.some(p => p === undefined)) { ok = false; break; }
      scores[section] = pts.reduce((a, b) => a + b, 0) / pts.length;
    }
    if (ok) {
      const lowest = Math.min(...Object.values(scores));
      return TIE_BREAK_ORDER.find(s => scores[s] === lowest);
    }
  }
  // Fallback: section_scores[...].score, same tie-break order.
  const sectionScores = result?.section_scores;
  if (sectionScores && typeof sectionScores === 'object') {
    const keyMap = { search_index: 'recruiter_search_index' };
    const vals = TIE_BREAK_ORDER
      .map(s => [s, sectionScores[keyMap[s] || s]?.score])
      .filter(([, v]) => typeof v === 'number');
    if (vals.length) {
      const lowest = Math.min(...vals.map(([, v]) => v));
      return vals.find(([, v]) => v === lowest)[0];
    }
  }
  return 'experience';
}

// ==========================================
// BOLD PARSER — turn **phrase** into <strong>phrase</strong>
// ==========================================
const renderBold = (text, boldColor) => {
  if (text === null || text === undefined) return null;
  const s = String(text);
  if (!s) return null;
  const parts = s.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => {
    if (p.startsWith('**') && p.endsWith('**') && p.length > 4) {
      // No hardcoded color by default — this renders inside both light
      // section cards (dark text) and the dark navy hero (light text), so
      // inheriting the parent's color plus font-weight is correct in most
      // places. The hero hook is the one spot that wants its own highlight
      // color (passed in as boldColor) to match the reference design.
      return <strong key={i} style={{ fontWeight: 700, color: boldColor }}>{p.slice(2, -2)}</strong>;
    }
    return p;
  });
};

// ==========================================
// WEAK WORDS — merge q1/q2/q3 weak_words into one deduped, non-fragmentary
// list, then highlight every occurrence of each surviving phrase in the
// real headline text with a soft-red background fill (redLite/redDeep,
// same pair KeywordChip already uses for "unmatched") — a flagged word
// should read as "this is the problem" at a glance, not a subtle underline.
// ==========================================
const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// Dedupes exact matches, then drops any phrase that is itself a substring
// of another surviving phrase (e.g. "EX -" is dropped in favor of the
// fuller "EX - Havas Media" flagged by another question). Capped at 5 after
// collapsing so a worst-case headline doesn't end up almost entirely
// underlined. Returns [{ word, severity }] in original q1->q2->q3 order.
const mergeWeakWords = (criteria) => {
  if (!Array.isArray(criteria)) return [];
  const severityByWord = {};
  criteria
    .filter(c => c && ['Q1', 'Q2', 'Q3'].includes(c.name) && Array.isArray(c.weak_words))
    .forEach(c => {
      c.weak_words.forEach(w => {
        if (typeof w !== 'string' || !w.trim()) return;
        if (severityByWord[w] !== 'WEAK') severityByWord[w] = c.verdict === 'WEAK' ? 'WEAK' : 'PARTIAL';
      });
    });

  const deduped = Object.keys(severityByWord);
  // Longest-first so shorter fragments get tested against fuller phrases
  // already kept, not the other way around.
  const byLength = [...deduped].sort((a, b) => b.length - a.length);
  const kept = [];
  for (const phrase of byLength) {
    if (kept.some(k => k.includes(phrase))) continue;
    kept.push(phrase);
  }
  // Restore original q1->q2->q3 encounter order for a stable, predictable render.
  return deduped
    .filter(w => kept.includes(w))
    .slice(0, 5)
    .map(w => ({ word: w, severity: severityByWord[w] }));
};

const renderHighlight = (text, weakWords) => {
  if (text === null || text === undefined) return null;
  const s = String(text);
  if (!s) return null;
  if (!Array.isArray(weakWords) || weakWords.length === 0) return s;

  // Longest-first in the alternation — JS regex alternation is first-match
  // -wins per position, not longest-match.
  const ordered = [...weakWords].sort((a, b) => b.word.length - a.word.length);
  const severityByMatch = new Map(ordered.map(w => [w.word, w.severity]));
  const pattern = ordered.map(w => escapeRegExp(w.word)).join('|');
  if (!pattern) return s;

  const parts = s.split(new RegExp(`(${pattern})`, 'g'));
  return parts.map((p, i) => {
    const severity = severityByMatch.get(p);
    if (severity) {
      return (
        <span
          key={i}
          style={{ background: c.redLite, color: c.redDeep, borderRadius: 3, padding: '1px 3px' }}
        >
          {p}
        </span>
      );
    }
    return p;
  });
};

// ==========================================
// KEYWORD CHIP — found/missing pill, first-2-missing blurred+locked
// (behavior unchanged from prior version)
// ==========================================
const KeywordChip = ({ keyword, matched, foundIn = [], blurred = false }) => (
  <div
    style={{
      display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px',
      borderRadius: 100, fontSize: 12.5, fontWeight: 600,
      background: matched ? c.greenLite : c.redLite,
      color: matched ? c.greenDeep : c.redDeep,
      border: `1px solid ${matched ? '#bbf7d0' : '#fecaca'}`,
    }}
    data-testid={blurred ? 'keyword-chip-blurred' : 'keyword-chip'}
  >
    {matched ? <Check size={13} /> : <X size={13} />}
    <span style={{ filter: blurred ? 'blur(4.5px)' : undefined, userSelect: blurred ? 'none' : undefined }} aria-hidden={blurred || undefined}>
      {keyword}
    </span>
    {matched && foundIn.length > 0 && <span style={{ opacity: 0.75, fontWeight: 500 }}>&middot; {foundIn.join(', ')}</span>}
    {blurred && <Lock size={11} style={{ marginLeft: 2 }} aria-label="Locked" />}
  </div>
);

// ==========================================
// FAQ ACCORDION ITEM — click-to-expand (new interaction, same content)
// ==========================================
const FAQItem = ({ question, answer, open, onToggle }) => (
  <div style={{ borderBottom: `1px solid ${c.bgFaint}` }} data-testid="faq-item">
    <button
      onClick={onToggle}
      style={{
        width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
        padding: '16px 4px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit',
      }}
    >
      <span style={{ fontSize: 14.5, fontWeight: 600, color: c.text }} data-testid="faq-question">{question}</span>
      <ChevronDown size={16} style={{ color: c.purple, flexShrink: 0, transition: 'transform .25s', transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }} />
    </button>
    {open && (
      <p style={{ padding: '0 4px 16px', fontSize: 13.5, lineHeight: 1.7, color: c.textSoft }} data-testid="faq-answer">{answer}</p>
    )}
  </div>
);

// ==========================================
// SCORE METER — animated count-up, gradient fill, tier ticks + labels
// (same tiers/boundaries/data as the prior ScoreBar: 0/40/61/78)
// ==========================================
const TIER_COLOR = { red: c.red, orange: c.amber, amber: c.amber, green: c.green };

const ScoreMeter = ({ total, label, labelColor }) => {
  const [animated, setAnimated] = useState(0);
  useEffect(() => {
    const target = total || 0;
    const t0 = performance.now();
    const dur = 1400;
    let raf;
    const tick = (t) => {
      const p = Math.min(1, (t - t0) / dur);
      const ease = 1 - Math.pow(1 - p, 3);
      setAnimated(target * ease);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [total]);

  // Badge text/color come from the backend's own get_score_label() (source
  // of truth) — never re-derived on the frontend. Boundaries below (<=40,
  // <=60, <=77, >77) are for the axis tick labels only and mirror that same
  // function exactly, matching the prior ScoreBar's tiers array.
  const tierColor = TIER_COLOR[labelColor] || c.textDim;

  const tiers = [
    { name: 'Invisible', boundary: 0, active: total <= 40 },
    { name: 'Getting Skipped', boundary: 40, active: total > 40 && total <= 60 },
    { name: 'Lost in the Crowd', boundary: 61, active: total > 60 && total <= 77 },
    { name: 'Search Ready', boundary: 78, active: total > 77 },
  ];

  return (
    <div style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(167,139,250,0.25)', borderRadius: 16, padding: '24px 20px' }} data-testid="score-bar-section">
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 52, fontWeight: 800, color: '#fff', lineHeight: 1, fontVariantNumeric: 'tabular-nums' }} data-testid="score-total">
          {Math.floor(animated)}
        </span>
        <span style={{ fontSize: 20, fontWeight: 600, color: c.textDim }}>/100</span>
        {label && (
          <span
            style={{ padding: '3px 12px', borderRadius: 100, fontSize: 13, fontWeight: 700, background: `${tierColor}22`, color: tierColor }}
            data-testid="score-tier-badge"
          >
            Your profile is {label}
          </span>
        )}
      </div>

      <div style={{ marginTop: 20, position: 'relative', height: 10, borderRadius: 100, background: 'rgba(255,255,255,0.12)' }}>
        <div
          style={{ position: 'absolute', inset: 0, borderRadius: 100, overflow: 'hidden' }}
        >
          <div
            style={{ height: '100%', borderRadius: 100, width: `${Math.min(100, animated)}%`, background: 'linear-gradient(90deg,#ef4444,#f59e0b,#22c55e)', transition: 'width 60ms linear' }}
            data-testid="score-bar-fill"
          />
        </div>
        <div style={{ position: 'absolute', left: '78%', top: -3, bottom: -3, width: 2, background: 'rgba(74,222,128,.8)', borderRadius: 2 }} data-testid="search-ready-marker" />
      </div>

      {/* Tier labels are absolutely positioned at their boundary %, which
          collides below ~520px: the 61% and 78% anchors are only 17% apart
          (~50px on a 375px screen) while each label needs 60-76px. The
          sl-tier-low class puts alternating labels on a second row so
          horizontally-adjacent labels never share one — see the media query
          in EvaluationReportView's root <style> block. Desktop is unaffected. */}
      <div className="sl-tier-axis" style={{ position: 'relative', marginTop: 10, height: 36, fontSize: 10, fontWeight: 600, letterSpacing: '.04em', color: '#f1eefa', textTransform: 'uppercase' }}>
        {tiers.map((t, i) => {
          const align = t.boundary === 0 ? 'left' : t.boundary >= 78 ? 'right' : 'center';
          const translate = align === 'left' ? 'translateX(0)' : align === 'right' ? 'translateX(-100%)' : 'translateX(-50%)';
          return (
            <span
              key={i}
              className={`sl-tier${i % 2 === 1 ? ' sl-tier-low' : ''}`}
              style={{ position: 'absolute', left: `${t.boundary}%`, transform: translate, textAlign: align, color: t.boundary === 78 ? c.green : '#f1eefa', maxWidth: 76, fontWeight: t.active ? 700 : 600 }}
              data-testid={`tier-label-${t.boundary}`}
            >
              {t.name}<br /><span style={{ opacity: 0.7 }}>{t.boundary}{t.boundary === 78 ? '+' : ''}</span>
            </span>
          );
        })}
        {/* Plain 100 endpoint — matches the reference template's axis, which
            marks the scale's right edge separately from the "Search Ready 78+"
            tier label just to its left. */}
        <span style={{ position: 'absolute', right: 0, top: 0, textAlign: 'right', opacity: 0.7 }}>100</span>
      </div>
    </div>
  );
};

// ==========================================
// SECTION CARD — icon badge + colored status pill + animated bar fill on
// scroll-in + lead/bullets or paragraph text (renders one card from
// section_voice, same guards/behavior as the prior SectionBlock)
// ==========================================
const PARAGRAPH_CARDS = new Set(['keywords']);

const SectionCard = ({ sKey, sectionScores, sectionVoice, currentHeadline }) => {
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting) { setVisible(true); io.disconnect(); } },
      { threshold: 0.25 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const voice = sectionVoice ? sectionVoice[sKey] : null;
  if (sKey !== 'headline' && (voice === null || voice === undefined)) return null;

  const label = SECTION_LABELS[sKey];
  const sectionData = sectionScores[sKey] || {};
  const score = sectionData.score ?? 0;
  const max = sectionData.max ?? 0;
  const { emoji, text: statusText, kind } = parseStateLabel(sectionData.state_label);
  const kindStyle = KIND_STYLES[kind];

  // Underline 1-4 weak words in the real headline, but only when the
  // section actually scored below 80% — a headline that's already scoring
  // well shouldn't get flagged words on it.
  const headlinePercentage = sectionData.percentage ?? 0;
  const weakWords = (sKey === 'headline' && headlinePercentage < 80)
    ? mergeWeakWords(sectionData.criteria)
    : [];

  const isParagraph = PARAGRAPH_CARDS.has(sKey);
  const paragraphText = isParagraph ? (typeof voice === 'string' ? voice : '') : '';
  const lead = !isParagraph && voice && typeof voice === 'object' ? (voice.lead || '') : '';
  const bullets = !isParagraph && voice && typeof voice === 'object' && Array.isArray(voice.bullets)
    ? voice.bullets.filter(b => typeof b === 'string' && b.trim())
    : [];

  if (sKey !== 'headline' && isParagraph && !paragraphText.trim()) return null;
  if (sKey !== 'headline' && !isParagraph && !lead.trim() && bullets.length === 0) return null;

  return (
    <div
      ref={ref}
      style={{ background: c.bgWhite, border: `1px solid ${c.bgFaint}`, borderRadius: 16, padding: '24px 20px', boxShadow: '0 1px 3px rgba(26,12,56,.05)' }}
      data-testid={`section-block-${sKey}`}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{
            width: 52, height: 52, flex: 'none', borderRadius: 15,
            background: 'linear-gradient(145deg, #2e1a5e 0%, #1a0c38 100%)',
            boxShadow: 'inset 0 1.5px 0 rgba(255,255,255,.22), inset 0 -2px 6px rgba(0,0,0,.4), 0 6px 14px rgba(26,12,56,.28)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <span style={{ fontSize: 26, lineHeight: 1, filter: 'drop-shadow(0 3px 5px rgba(0,0,0,.45))' }}>{emoji}</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5, alignItems: 'flex-start' }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: c.text }}>{label}</div>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 10px', borderRadius: 100, fontSize: 11.5, fontWeight: 700, background: kindStyle.badgeBg, color: kindStyle.badgeColor }}>
              {statusText}
            </div>
          </div>
        </div>
        <span style={{ fontSize: 14, fontWeight: 700, color: c.textMute }} data-testid={`section-score-${sKey}`}>
          {Math.floor(score)}<span style={{ color: c.textDim, fontWeight: 500 }}>/{max}</span>
        </span>
      </div>

      <div style={{ marginTop: 12, height: 6, borderRadius: 100, background: c.bgFaint, overflow: 'hidden' }}>
        <div style={{ height: '100%', borderRadius: 100, background: kindStyle.bar, width: visible && max ? `${Math.min(100, (score / max) * 100)}%` : '0%', transition: 'width 1.1s cubic-bezier(.22,1,.36,1)' }} />
      </div>

      {sKey === 'headline' && (
        <div style={{ marginTop: 14, padding: '12px 14px', borderRadius: 10, background: c.bgFaint }} data-testid="current-headline-block">
          <p style={{ margin: 0, marginBottom: 6, fontSize: 12.5, fontWeight: 700, color: c.textSoft }}>
            Here's your current headline — let's look at what recruiters actually see.
          </p>
          {currentHeadline && currentHeadline.trim() ? (
            <p style={{ margin: 0, fontSize: 14, lineHeight: 1.55, color: c.text, fontStyle: 'italic' }}>
              "{renderHighlight(currentHeadline, weakWords)}"
            </p>
          ) : (
            <p style={{ margin: 0, fontSize: 14, lineHeight: 1.55, color: c.text }}>
              No headline detected on your profile — this is the first thing to fix.
            </p>
          )}
        </div>
      )}

      {isParagraph ? (
        <p style={{ marginTop: 14, color: c.textMid, fontSize: 14, lineHeight: 1.65 }} data-testid={`section-voice-${sKey}`}>
          {renderBold(paragraphText)}
        </p>
      ) : (
        <>
          {lead.trim() && (
            <p style={{ marginTop: 14, color: c.text, fontSize: 14, lineHeight: 1.55, fontWeight: 500 }} data-testid={`section-voice-lead-${sKey}`}>
              {renderBold(lead)}
            </p>
          )}
          {bullets.length > 0 && (
            <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }} data-testid={`section-voice-bullets-${sKey}`}>
              {bullets.map((b, idx) => (
                <div key={idx} style={{ display: 'flex', gap: 10, fontSize: 13.5, lineHeight: 1.6, color: c.textMid }}>
                  <span style={{ color: c.purpleSoft, flex: 'none', fontWeight: 800, fontSize: 17, lineHeight: 1.2 }}>&bull;</span>
                  <span>{renderBold(b)}</span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ==========================================
// MAIN REPORT VIEW — pure function of `data`, no data-loading opinion
// ==========================================
const EvaluationReportView = ({ data }) => {
  const navigate = useNavigate();
  const [redirecting, setRedirecting] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);
  // null means BOTH "fetch still in flight" and "fetch failed" — in either
  // case the price block renders nothing at all. Deliberately not seeded with
  // a default the way CheckoutPage.jsx does: a stale figure quoted here that
  // checkout then contradicts is worse than showing no price.
  const [pricing, setPricing] = useState(null);

  useEffect(() => {
    document.title = 'Your LinkedIn Evaluation, Successlane';
    // Poppins is already loaded globally (public/index.html) for the
    // redesigned pages — no per-page font <link> needed here.
  }, []);

  // Same admin-controlled source checkout reads (CheckoutPage.jsx). The
  // typeof guard means a malformed 200 leaves pricing null rather than
  // rendering "₹undefined"; a rejected fetch does the same via .catch.
  useEffect(() => {
    fetch(`${API_URL}/api/pricing/current`)
      .then(r => r.json())
      .then(d => { if (typeof d?.price === 'number') setPricing(d); })
      .catch(() => {});
  }, []);

  // Funnel telemetry: this page is the moment of truth in the free funnel —
  // the user has their score and either buys or leaves. Both the view and how
  // long they stayed are recorded. trackDwell returns its own cleanup, so
  // returning it directly registers the unmount handler.
  useEffect(() => {
    trackEvent('report_viewed');
    return trackDwell('report_dwell');
  }, []);

  const handleGetRewrite = () => {
    setRedirecting(true);
    const s = result?.score?.total;
    if (s) sessionStorage.setItem('evaluationScore', s);
    trackEvent('checkout_cta_clicked', { metadata: { score: s ?? null, source: 'report' } });
    navigate('/checkout', { replace: true });
  };

  // Extract v5 data (from analysis_result field or top-level) — unchanged
  const result = data.analysis_result || data;
  const profile = result.profile || {};
  const target = result.target || {};
  const score = result.score || { total: 0, max: 100 };
  const sectionScores = result.section_scores || {};
  const kw = result.keywords || {};
  const searchRankings = result.search_rankings || {};
  // section_voice: null → backend LLM call failed after retry. {} → success but
  // empty (shouldn't happen). object with keys → success. Preserve the null
  // signal so the failure banner can fire; downstream reads default to {}.
  const sectionVoiceFailed = result.section_voice === null;
  const sectionVoice = result.section_voice || {};
  const beforeAfter = result.before_after || {};
  // Same field path the (now-hidden) before/after block used: structured_data
  // .headline falling back to .current_title — see lines 751/763.
  const structuredData = result.structured_data || {};
  const currentHeadline = (structuredData.headline && structuredData.headline.trim())
      ? structuredData.headline
      : '';
  const faq = result.faq || [];
  // profile.first_name is always populated by the backend (falls back to the
  // literal "there" if the source name was missing) — this local fallback is
  // extra defense against older/malformed rows, deriving the same way the
  // backend itself does: first whitespace-delimited token of the full name.
  const firstName = profile.first_name || (profile.name || '').trim().split(/\s+/)[0] || '';
  const weakestSection = computeWeakestSection(result);
  const targetRoleDetected = result.target_role_detected || target.role || data.userInput?.targetRole || '';
  const displayedRole = displayRole(targetRoleDetected);

  const defaultFaq = [
    { question: "How is this different from ChatGPT or Claude?", answer: "Generic AI rewrites don't know which specific roles you're targeting or which companies you want to join. We take your resume, target roles, and dream company JDs, then rebuild your profile around the exact keywords and signals recruiters hiring for those roles actually search for. Every line is then reviewed and signed off by a working recruiter in your industry. That certification is something generic AI can't replicate." },
    { question: "What if I don't have dream company JDs?", answer: "No problem. Share 2-3 companies you'd love to work at, and we'll pull the JDs ourselves for your target role. You can also send us roles you've bookmarked on LinkedIn or Naukri, whichever is easiest." },
    { question: "Who is the recruiter? Can I see their profile?", answer: "Each profile is certified by a recruiter who has personally hired for your role type in your industry within the last 3 years. You'll see their name and LinkedIn profile in your delivery email. We also show a few of our panel recruiters on this page above." },
    { question: "Will LinkedIn flag or penalize my profile?", answer: "No. We rewrite content and optimize for search. We don't use bots, automation, or anything that violates LinkedIn's terms. The changes we make are standard profile hygiene practices recommended by LinkedIn itself for professional visibility." },
    { question: "Can I edit the rewrite after delivery?", answer: "Absolutely. You receive 2 PDFs, your optimized profile and the LinkedIn Playbook, and you can edit freely. You also get one round of free revisions within 7 days if anything needs adjusting." },
    { question: "What if the quality isn't as promised?", answer: "Our quality match guarantee: if you find recruiter-certified rewrites of this quality anywhere else at our price, we'll make yours free. We can offer this because our work is personally reviewed by a working recruiter, not AI-generated filler." },
  ];
  const faqItems = faq.length > 0 ? faq : defaultFaq;

  return (
    <div className="sl-report" style={{ minHeight: '100vh', background: c.bgLavender, WebkitFontSmoothing: 'antialiased' }} data-testid="evaluation-report">
      <style>{`
        .sl-report, .sl-report * { font-family: 'Poppins', -apple-system, BlinkMacSystemFont, sans-serif; }
        .sl-report a { color: ${c.purple}; }
        @media print { .sl-report .sl-no-print { display: none !important; } }
        /* Score-meter axis: stagger tier labels onto two rows on narrow
           screens so "Lost in the Crowd" (61%) and "Search Ready" (78%) stop
           overlapping. !important is needed on height/max-width only because
           those two carry inline styles; top and font-size do not. */
        @media (max-width: 520px) {
          .sl-report .sl-tier-axis { height: 54px !important; }
          .sl-report .sl-tier { font-size: 8px; letter-spacing: normal; white-space: nowrap; max-width: none !important; }
          .sl-report .sl-tier-low { top: 26px; }
        }
      `}</style>

      {/* Top bar */}
      <div style={{ width: '100%', background: c.navy, display: 'flex', justifyContent: 'center', position: 'sticky', top: 0, zIndex: 50 }}>
        <div style={{ width: '100%', maxWidth: 880, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', boxSizing: 'border-box' }}>
          <div
            style={{ background: '#fff', borderRadius: 8, padding: '5px 10px', display: 'inline-flex', alignItems: 'center', lineHeight: 0, cursor: 'pointer' }}
            onClick={() => navigate('/')}
          >
            <img src="/images/brand/successlane-logo-mark.png" alt="Successlane" style={{ height: 18, width: 'auto', display: 'block' }} />
          </div>
          <span style={{ fontSize: 12, color: '#c4b5fd', fontWeight: 500 }}>Evaluated on {result.meta?.evaluated_on || new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
        </div>
      </div>

      <div style={{ maxWidth: 880, margin: '0 auto', padding: '0 20px' }}>

        {/* RETURNING USER BANNER */}
        {data?.returning && (
          <div style={{ paddingTop: 24 }} data-testid="returning-user-banner">
            {/* Purple, not amber: amber reads as a warning, and this is a
                conversion moment. Background is bgFaint rather than bgLavender
                because bgLavender is the page background — the card would
                dissolve into it. CTA button below is left untouched so it
                stays the strongest element here. */}
            <div style={{ background: c.bgFaint, border: `1.5px solid ${c.purple}`, borderTop: `4px solid ${c.purple}`, borderRadius: 16, padding: 24 }}>
              <p style={{ fontWeight: 700, fontSize: 16, color: c.text, margin: 0 }}>
                {firstName ? `${firstName}, you've already seen where your profile stands.` : "You've already seen where your profile stands."}
              </p>
              <p style={{ fontSize: 13, fontWeight: 600, color: c.purpleDeep, marginTop: 16, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '.04em' }}>
                The gap is still open:
              </p>
              <blockquote style={{ margin: 0, padding: '4px 0 4px 16px', borderLeft: `3px solid ${c.purple}`, color: c.textMid, fontSize: 14.5, lineHeight: 1.6, fontStyle: 'italic' }}>
                {SECTION_GAP_LINES[weakestSection]}
              </blockquote>
              <p style={{ fontSize: 14, color: c.text, lineHeight: 1.7, marginTop: 16 }}>
                You have a strong career. It just is NOT showing up in recruiter search. That part is fixable.
              </p>
              <p style={{ fontSize: 14, color: c.text, lineHeight: 1.7, marginBottom: 16 }}>
                A recruiter who hires for your domain rewrites it and certifies it in 48 hours.
              </p>
              <button
                onClick={handleGetRewrite}
                disabled={redirecting}
                style={{ width: '100%', background: c.gradient, color: '#fff', fontSize: 15, fontWeight: 700, minHeight: 48, borderRadius: 10, cursor: 'pointer', border: 'none', opacity: redirecting ? 0.7 : 1 }}
              >
                {redirecting ? 'Redirecting...' : 'Get my profile rewritten'}
              </button>
              <button
                onClick={() => document.getElementById('report-hero')?.scrollIntoView({ behavior: 'smooth' })}
                style={{ display: 'block', margin: '12px auto 0', background: 'none', border: 'none', color: c.textMute, fontSize: 12.5, cursor: 'pointer', textDecoration: 'underline' }}
              >
                Read your full report
              </button>
            </div>
          </div>
        )}

        {/* SECTION VOICE FAILURE BANNER */}
        {sectionVoiceFailed && (
          <div style={{ paddingTop: 24 }} data-testid="section-voice-failure-banner">
            <div style={{ background: c.bgWhite, border: `1px solid ${c.bgFaint}`, borderRadius: 12, padding: '20px 24px' }}>
              <p style={{ fontSize: 14, color: c.textMid, lineHeight: 1.65, margin: 0 }}>
                Your recruiter write-up is still being prepared. Your score and keyword coverage are below. Check back in a few minutes and the full read will be here.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* HERO — dark navy verdict block, continues from the top bar */}
      <div id="report-hero" style={{ width: '100%', background: `radial-gradient(1200px 600px at 50% -100px, ${c.navyMid} 0%, ${c.navy2} 42%, ${c.navy} 100%)`, backgroundColor: c.navy, display: 'flex', justifyContent: 'center' }}>
        <div style={{ width: '100%', maxWidth: 880, padding: '36px 20px 48px', boxSizing: 'border-box' }}>
          {!sectionVoiceFailed && (
            <div data-testid="hero-section">
              <p style={{ fontSize: 15, fontWeight: 700, letterSpacing: '.12em', color: '#a78bfa', textTransform: 'uppercase', marginBottom: 16 }} data-testid="hero-eyebrow">
                Your LinkedIn Evaluation
              </p>
              {typeof sectionVoice.hook === 'string' && sectionVoice.hook.trim() && (
                <p style={{ fontSize: 'clamp(15px,2.2vw,18px)', fontWeight: 500, lineHeight: 1.75, color: '#ddd8ee', margin: 0 }} data-testid="hero-hook">
                  {renderBold(sectionVoice.hook, '#c4b5fd')}
                </p>
              )}
              {target.role_inferred && target.inferred_note && (
                <div style={{ marginTop: 16, padding: 12, borderRadius: 10, fontSize: 13, background: 'rgba(245,158,11,.15)', color: c.yellow }}>
                  {target.inferred_note}
                </div>
              )}
            </div>
          )}

          <div style={{ marginTop: 36 }}>
            <ScoreMeter total={score.total || 0} label={score.label} labelColor={score.label_color} />
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 880, margin: '0 auto', padding: '0 20px' }}>

        {/* SECTION SCORES */}
        {!sectionVoiceFailed && (
          <div style={{ paddingTop: 40 }}>
            <div style={{ fontSize: 15, fontWeight: 700, letterSpacing: '.12em', color: c.purpleDeep, textTransform: 'uppercase', marginBottom: 20 }} data-testid="section-blocks-heading">
              What recruiters see when they open your profile
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }} data-testid="section-blocks">
              {SECTION_KEYS.map(sKey => (
                <React.Fragment key={sKey}>
                  <SectionCard sKey={sKey} sectionScores={sectionScores} sectionVoice={sectionVoice} currentHeadline={currentHeadline} />

                  {sKey === 'keywords' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                      {[
                        { key: 'skill', title: `Core keywords recruiters search for ${displayedRole || 'your target role'}`, desc: 'These are the words typed into the search box when someone is hiring for your target role.', legend: true, matches: kw.skill?.matches || [], footer: kw.skill?.footer },
                        { key: 'bonus', title: 'High priority keywords', desc: 'These keywords increase your profile shortlist.', legend: false, matches: kw.bonus?.matches || [], footer: null },
                      ].map(grid => {
                        const blurIdx = new Set();
                        for (let i = 0; i < grid.matches.length && blurIdx.size < 2; i++) {
                          const m = grid.matches[i];
                          const isMatched = m.matched ?? m.found;
                          if (!isMatched) blurIdx.add(i);
                        }
                        return (
                          <div key={grid.key}>
                            <h3 style={{ fontSize: 12.5, fontWeight: 700, letterSpacing: '.1em', color: c.purpleDeep, textTransform: 'uppercase', marginBottom: 10 }}>{grid.title}</h3>
                            {grid.desc && <p style={{ fontSize: 13.5, color: c.textSoft, marginBottom: 10 }}>{grid.desc}</p>}
                            {/* Legend maps chip colours to meaning — only under the
                                core-keywords grid, where the chips first appear.
                                Reuses KeywordChip's own green/red tokens so the
                                words match the chips they describe. */}
                            {grid.legend && (
                              <div style={{ fontSize: 13.5, color: c.textSoft, marginBottom: 10, display: 'flex', flexDirection: 'column', gap: 4 }} data-testid="keyword-legend">
                                <span><strong style={{ color: c.greenDeep }}>Green</strong> = already on your profile</span>
                                <span><strong style={{ color: c.redDeep }}>Red</strong> = missing, so the search skips you</span>
                                <span><strong>Blurred</strong> = unlocked with your rewrite</span>
                              </div>
                            )}
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                              {grid.matches.map((m, i) => (
                                <KeywordChip
                                  key={i}
                                  keyword={m.keyword}
                                  matched={m.matched ?? m.found}
                                  foundIn={m.found_in || m.placements || []}
                                  blurred={blurIdx.has(i)}
                                />
                              ))}
                            </div>
                            {grid.footer && <p style={{ fontSize: 13.5, color: c.text, marginTop: 12 }}>{grid.footer}</p>}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {sKey === 'experience' && (
                    <p style={{ color: c.text, fontWeight: 600, fontSize: 14, lineHeight: 1.6, margin: 0 }} data-testid="experience-to-search-bridge">
                      So here is where all of that leaves you.
                    </p>
                  )}

                  {sKey === 'recruiter_search_index' && (
                    <div style={{ background: c.navy, borderRadius: 20, padding: '28px 24px', boxSizing: 'border-box' }} data-testid="search-rankings-section">
                      <h3 style={{ fontSize: 12.5, fontWeight: 700, letterSpacing: '.1em', color: '#a78bfa', textTransform: 'uppercase', marginBottom: 8 }}>We search. Here is where you land.</h3>
                      <p style={{ fontSize: 13.5, color: '#c5bfe0', lineHeight: 1.6, marginBottom: 18 }}>These are the searches we run for this position. This is the page you appear on.</p>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                        {(searchRankings.searches || []).map((s, i) => (
                          <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 12, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(167,139,250,0.2)', borderRadius: 14, padding: 16, boxSizing: 'border-box' }} data-testid={`search-bar-${i}`}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12, background: '#fff', borderRadius: 100, padding: '8px 8px 8px 18px' }}>
                              <Search size={17} style={{ color: c.purpleDeep, flexShrink: 0 }} />
                              <span style={{ flex: 1, fontSize: 14.5, fontWeight: 500, color: c.text, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.query}</span>
                              <span style={{ flexShrink: 0, background: c.purpleDeep, color: '#fff', fontSize: 12.5, fontWeight: 700, padding: '8px 20px', borderRadius: 100, whiteSpace: 'nowrap' }}>Search</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
                              <span style={{ fontSize: 11.5, fontWeight: 600, color: '#c5bfe0', marginRight: 3 }}>Results page</span>
                              {['1', '2', '3', '···'].map((label, pi) => (
                                <span key={pi} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 26, height: 26, padding: '0 7px', boxSizing: 'border-box', borderRadius: 8, background: 'rgba(255,255,255,0.08)', color: '#c5bfe0', fontSize: 12, fontWeight: 600 }}>{label}</span>
                              ))}
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(252,165,165,0.35)', borderRadius: 100, padding: '4px 12px 4px 4px' }}>
                                <span style={{ width: 22, height: 22, borderRadius: 100, background: c.purpleSoft, color: '#fff', fontSize: 9, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                  {getInitials(profile.name || profile.first_name)}
                                </span>
                                <span style={{ fontSize: 12, fontWeight: 700, color: '#fca5a5', whiteSpace: 'nowrap' }}>Your profile &middot; {s.page_placement}</span>
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                      <p style={{ fontSize: 11.5, color: '#9691a8', fontStyle: 'italic', marginTop: 14 }} data-testid="search-intelligence-disclaimer-rankings">Based on Successlane Search Intelligence</p>
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        )}

        {/* MOVEMENT: DIAGNOSIS → TURN */}
        <hr style={{ border: 'none', borderTop: `1px solid ${c.bgFaint}`, margin: '40px 0' }} data-testid="movement-divider-turn" />

        {/* WHY THIS HASN'T FIXED ITSELF — merges what were three separate
            argument sections (the old bridge block, the two-options cards, and
            the value-stack grid) into one. The closing cost-of-inaction line
            was previously inside the Option B card. */}
        <div style={{ background: c.bgFaint, borderRadius: 20, padding: '32px 24px', boxSizing: 'border-box', marginBottom: 32 }} data-testid="bridge-block">
          <p style={{ fontSize: 12.5, fontWeight: 700, letterSpacing: '.1em', color: c.purpleDeep, textTransform: 'uppercase', marginBottom: 14 }}>
            Why this hasn't fixed itself
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14, fontSize: 15, lineHeight: 1.7 }}>
            <p style={{ margin: 0, color: c.textMid }}>You have probably rewritten this profile more than once. It didn't change your ranking, because the ranking is decided by search terms nobody told you were being used.</p>
            <p style={{ margin: 0, color: c.textMid }}>A recruiter who fills these roles every week knows which version survives being read by another recruiter.</p>
            <p style={{ margin: 0, color: c.text, fontWeight: 600 }} data-testid="cta-cost-of-inaction">
              Recruiters hiring for <strong>{displayedRole || 'your target role'}</strong> shortlist someone else every week you wait.
            </p>
          </div>
        </div>

        {/* MOVEMENT: TURN → PROOF */}
        <hr style={{ border: 'none', borderTop: `1px solid ${c.bgFaint}`, margin: '40px 0' }} data-testid="movement-divider-proof" />

        {/* BEFORE / AFTER — hidden, not deleted; see instruction to stop rendering this card */}
        {false && (
        <div style={{ marginBottom: 32 }}>
          <div style={{ fontSize: 15, fontWeight: 700, letterSpacing: '.12em', color: c.purpleDeep, textTransform: 'uppercase', marginBottom: 10 }}>The same career, written to be found</div>
          <p style={{ fontSize: 13.5, color: c.textSoft, marginBottom: 20 }}>
            Here is how your headline gets restructured. Your rewrite covers every section.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(260px,1fr))', gap: 16 }}>
            <div style={{ borderRadius: 16, overflow: 'hidden', border: `1.5px solid #fecaca`, background: c.bgWhite }}>
              <div style={{ background: c.redLite, padding: '8px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.12em', color: c.redDeep }}>BEFORE</span>
                <span style={{ fontSize: 16 }}>🔴</span>
              </div>
              <div style={{ padding: 18 }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: c.redDeep }}>Headline</span>
                <p style={{ fontSize: 14, marginTop: 6, color: c.text, lineHeight: 1.5 }}>
                  {beforeAfter.headline?.before_headline || profile.current_title || 'No headline set'}
                </p>
              </div>
            </div>
            <div style={{ borderRadius: 16, overflow: 'hidden', border: `1.5px solid #bbf7d0`, background: c.bgWhite, boxShadow: '0 8px 24px rgba(34,197,94,.12)' }}>
              <div style={{ background: c.greenLite, padding: '8px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.12em', color: c.greenDeep }}>AFTER</span>
                <span style={{ fontSize: 16 }}>🟢</span>
              </div>
              <div style={{ padding: 18 }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: c.greenDeep }}>Headline</span>
                <p style={{ fontSize: 14, marginTop: 6, color: c.text, lineHeight: 1.5, fontWeight: 600 }}>
                  {beforeAfter.headline?.after_headline || `${profile.current_title || 'Professional'} | Strategy & Leadership`}
                </p>
              </div>
            </div>
          </div>
        </div>
        )}

        {/* TESTIMONIALS */}
        <div style={{ marginBottom: 32 }} data-testid="testimonials-section">
          <div style={{ fontSize: 15, fontWeight: 700, letterSpacing: '.12em', color: c.purpleDeep, textTransform: 'uppercase', marginBottom: 20 }}>Some of our success stories</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(260px,1fr))', gap: 14 }}>
            <div style={{ background: c.bgWhite, border: `1px solid ${c.bgFaint}`, borderRadius: 16, padding: '22px 20px', boxShadow: '0 1px 3px rgba(26,12,56,.05)' }} data-testid="testimonial-krishna">
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
                <div style={{ width: 40, height: 40, borderRadius: 100, background: '#ede9fe', color: c.purpleDeep, fontWeight: 700, fontSize: 15, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>K</div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: c.text }}>Krishna Sai Pavan</div>
                  <div style={{ fontSize: 12, color: c.greenDeep, fontWeight: 600 }}>Data Analytics Lead, Boeing</div>
                </div>
              </div>
              <p style={{ fontSize: 13.5, lineHeight: 1.7, color: c.textMid, margin: 0 }}>"I had the experience. The calls just weren't coming. After the rewrite, the offer came through in six weeks, with a 44% hike."</p>
            </div>
            <div style={{ background: c.bgWhite, border: `1px solid ${c.bgFaint}`, borderRadius: 16, padding: '22px 20px', boxShadow: '0 1px 3px rgba(26,12,56,.05)' }} data-testid="testimonial-shruthi">
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
                <div style={{ width: 40, height: 40, borderRadius: 100, background: '#ede9fe', color: c.purpleDeep, fontWeight: 700, fontSize: 15, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>S</div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: c.text }}>Shruthi N</div>
                  <div style={{ fontSize: 12, color: c.greenDeep, fontWeight: 600 }}>Chennai to Bengaluru</div>
                </div>
              </div>
              <p style={{ fontSize: 13.5, lineHeight: 1.7, color: c.textMid, margin: 0 }}>"Eighteen months trying to move cities. I had no idea how much of it came down to how recruiters search. Thirty days after the rewrite, two offers, one of them Swiggy."</p>
            </div>
          </div>
        </div>

        {/* MOVEMENT: PROOF → OFFER */}
        <hr style={{ border: 'none', borderTop: `1px solid ${c.bgFaint}`, margin: '40px 0' }} data-testid="movement-divider-offer" />

        {/* OFFER DETAIL */}
        <div style={{ background: c.navy, borderRadius: 20, padding: '32px 24px', boxSizing: 'border-box', marginBottom: 32, position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', top: -80, right: -80, width: 240, height: 240, borderRadius: '50%', background: 'radial-gradient(circle, rgba(124,58,237,.35), transparent 70%)' }} />
          <div style={{ position: 'relative' }}>
            {/* Hidden entirely until /api/pricing/current resolves, and stays
                hidden if it fails — showing no price is better than showing one
                checkout won't honour. compare_at_price is rendered bare (no
                "Market rate" label): the backend resolves it from either offer
                or market mode, so in offer mode it is our own former price,
                not the market's. */}
            {pricing && (
              <div style={{ padding: 16, borderRadius: 12, background: 'rgba(255,255,255,.05)', border: '1px solid rgba(255,255,255,.10)' }} data-testid="cta-price-reframe">
                <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                  <div>
                    {pricing.compare_at_price ? (
                      <span style={{ fontSize: 13, color: 'rgba(255,255,255,.55)', textDecoration: 'line-through', display: 'block' }}>
                        &#8377;{pricing.compare_at_price.toLocaleString('en-IN')}
                      </span>
                    ) : null}
                    <span style={{ fontSize: 32, fontWeight: 800, color: '#fff', display: 'block', marginTop: 4 }}>
                      &#8377;{pricing.price.toLocaleString('en-IN')}
                    </span>
                  </div>
                  <span style={{ fontSize: 12, color: 'rgba(255,255,255,.6)', textAlign: 'right' }}>one-time<br />no subscription</span>
                </div>
              </div>
            )}

            <div style={{ marginTop: 24, padding: 20, borderRadius: 14, background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.08)' }} data-testid="guarantee-section">
              <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', color: 'rgba(255,255,255,.55)' }}>If it is not right, we fix it</span>
              <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
                {[
                  "Not satisfied? We revise until it's right. Unlimited rounds in the first 7 days.",
                  "Find this quality at our price anywhere else, we make yours free.",
                  "Every line signed off by a working recruiter who hires for your role type.",
                ].map((g, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                    <Check size={14} style={{ color: c.green, marginTop: 3, flexShrink: 0 }} />
                    <span style={{ fontSize: 13.5, color: 'rgba(255,255,255,.85)', lineHeight: 1.5 }}>{g}</span>
                  </div>
                ))}
              </div>
            </div>

            <button
              onClick={handleGetRewrite}
              disabled={redirecting}
              style={{ marginTop: 20, width: '100%', background: c.green, color: '#052e16', fontWeight: 700, fontSize: 16, minHeight: 54, borderRadius: 12, cursor: 'pointer', border: 'none', boxShadow: '0 6px 20px rgba(34,197,94,.3)', opacity: redirecting ? 0.7 : 1 }}
              data-testid="cta-get-rewrite-btn"
            >
              {redirecting
                ? <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}><Loader2 style={{ width: 18, height: 18 }} className="animate-spin" />Redirecting to checkout...</span>
                : 'Get my recruiter-written profile'}
            </button>
            <p style={{ textAlign: 'center', marginTop: 12, color: 'rgba(255,255,255,.6)', fontSize: 12, lineHeight: 1.55 }}>
              Delivered in 48 hours, with a 100 day action plan to get you to your next role.
            </p>
          </div>
        </div>

        {/* FAQ */}
        <div style={{ marginBottom: 32 }} data-testid="faq-section">
          <div style={{ fontSize: 15, fontWeight: 700, letterSpacing: '.12em', color: c.purpleDeep, textTransform: 'uppercase', marginBottom: 20 }}>Questions before you buy</div>
          <div style={{ background: c.bgWhite, border: `1px solid ${c.bgFaint}`, borderRadius: 16, padding: '4px 18px' }}>
            {faqItems.map((item, i) => (
              <FAQItem key={i} question={item.question} answer={item.answer} open={openFaq === i} onToggle={() => setOpenFaq(openFaq === i ? -1 : i)} />
            ))}
          </div>
        </div>

        <div style={{ padding: '16px 0 32px', textAlign: 'center' }}>
          <span style={{ fontSize: 12, color: c.textDim }}>Successlane &middot; successlane.io</span>
        </div>
      </div>

      {/* Sticky CTA bar */}
      <div className="sl-no-print" style={{ position: 'fixed', bottom: 0, left: 0, right: 0, background: 'rgba(255,255,255,.95)', backdropFilter: 'blur(8px)', borderTop: `1px solid ${c.bgFaint}`, display: 'flex', justifyContent: 'center', zIndex: 999 }}>
        <div style={{ width: '100%', maxWidth: 880, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '12px 20px', boxSizing: 'border-box' }}>
          <span style={{ fontSize: 15, fontWeight: 800, color: score.total < 50 ? c.redDeep : c.amberDeep, whiteSpace: 'nowrap' }}>
            {Math.floor(score.total)}<span style={{ fontSize: 12, color: c.textDim, fontWeight: 600 }}>/100</span>
          </span>
          <button
            onClick={handleGetRewrite}
            disabled={redirecting}
            style={{ flex: 1, maxWidth: 360, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 46, padding: '10px 20px', boxSizing: 'border-box', background: c.purple, color: '#fff', fontWeight: 700, fontSize: 14, borderRadius: 10, border: 'none', cursor: 'pointer', opacity: redirecting ? 0.7 : 1 }}
            data-testid="sticky-cta-btn"
          >
            {redirecting ? 'Redirecting...' : 'Get my recruiter-written profile'}
          </button>
        </div>
      </div>
      <div style={{ height: 74 }} />
    </div>
  );
};

export default EvaluationReportView;
